package br.com.htms.inspeccaocampo.data.ativos

import br.com.htms.inspeccaocampo.auth.AuthApi
import br.com.htms.inspeccaocampo.data.JsonMap
import kotlin.math.abs
import kotlin.math.roundToLong

data class CentroCusto(
    val id: String,
    val codigo: String,
    val nome: String,
    val ativo: Boolean = true,
    /** Engenheiro/Supervisor que aprova as solicitações da obra (vazio = qualquer Engenheiro/Supervisor). */
    val engenheiroUsername: String = "",
    /** ADM da obra: faz as solicitações e responde às devolvidas. */
    val admUsername: String = ""
) {
    val rotulo: String get() = "$codigo - $nome"
}

/** Usuário liberado no Controle de Ativos (para escolher responsáveis). */
data class UsuarioAtivos(
    val username: String,
    val nome: String = "",
    val engSup: Boolean = false,
    val adm: Boolean = false,
    /** Liberado no Controle de Ativos (Config). */
    val liberado: Boolean = true,
    /** Role "admin" do sistema (grupo Administradores). */
    val admin: Boolean = false
) {
    val rotulo: String get() = if (nome.isNotBlank()) "$nome ($username)" else username
    val nomeOuLogin: String get() = nome.ifBlank { username }
}

data class SolicEvento(val em: Long, val por: String, val acao: String, val obs: String)

data class Solicitacao(
    val id: String,
    /** MOV (movimentação de ativo) ou LOCACAO (locação de veículo). */
    val tipo: String,
    val status: String,
    val centroCustoId: String = "",
    val ativoId: String = "",
    val movTipo: String = "",
    val paraCentroCustoId: String = "",
    val responsavel: String = "",
    val data: String = "",
    val observacao: String = "",
    val veiculo: String = "",
    val dataInicio: String = "",
    val dataFim: String = "",
    val justificativa: String = "",
    val condutor: String = "",
    val solicitante: String = "",
    val solicitanteNome: String = "",
    val historico: List<SolicEvento> = emptyList(),
    val createdAt: Long = 0L,
    val updatedAt: Long = 0L
) {
    val locacao: Boolean get() = tipo == "LOCACAO"
    val aberta: Boolean get() = status == "PENDENTE" || status == "DEVOLVIDA" || status == "AGUARDANDO_FROTAS"
    val solicitanteRotulo: String get() = if (solicitanteNome.isNotBlank()) "$solicitanteNome ($solicitante)" else solicitante
}

object SolicStatus {
    fun rotulo(id: String): String = when (id) {
        "PENDENTE" -> "Aguardando aprovação"
        "DEVOLVIDA" -> "Devolvida para confirmação"
        "REPROVADA" -> "Reprovada"
        "APROVADA" -> "Aprovada"
        "AGUARDANDO_FROTAS" -> "Aguardando Coord. de Frotas"
        "ATENDIDA" -> "Atendida"
        "CANCELADA" -> "Cancelada"
        else -> id
    }

    fun rotuloEvento(acao: String): String = when (acao) {
        "CRIADA" -> "Solicitado"
        "APROVADA" -> "Aprovado"
        "REPROVADA" -> "Reprovado"
        "DEVOLVIDA" -> "Devolvido para confirmação"
        "PENDENTE" -> "Reenviado"
        "CANCELADA" -> "Cancelado"
        "ATENDIDA" -> "Atendido pelo Coord. de Frotas"
        else -> acao
    }
}

data class Fornecedor(
    val id: String,
    val nome: String,
    val cnpj: String = "",
    val contato: String = "",
    val telefone: String = "",
    val email: String = "",
    val observacoes: String = "",
    val ativo: Boolean = true
)

data class Ativo(
    val id: String = "",
    val codigo: String = "",
    val descricao: String = "",
    val modelo: String = "",
    val categoria: String = "",
    val placaPatrimonio: String = "",
    val propriedade: String = "ALUGADO",
    val fornecedorId: String = "",
    val centroCustoId: String = "",
    val responsavel: String = "",
    /** yyyy-MM-dd */
    val dataChegada: String = "",
    /** yyyy-MM-dd */
    val dataInicioContrato: String = "",
    /** yyyy-MM-dd */
    val dataTerminoContrato: String = "",
    /** yyyy-MM-dd */
    val dataEntrega: String = "",
    val valorMes: Double? = null,
    val valorEquipamento: Double? = null,
    val valorEmAberto: Double? = null,
    val valorFinalCompra: Double? = null,
    val utilizacao: String = "",
    val inspecaoTipo: String = "",
    val status: String = "EM_USO",
    val observacoes: String = "",
    val updatedAt: Long = 0L
) {
    val alugado: Boolean get() = propriedade == "ALUGADO"

    /** Ainda gera custo / está sob responsabilidade da empresa. */
    val emPosse: Boolean get() = status != "DEVOLVIDO" && status != "BAIXADO"

    val valorDia: Double? get() = valorMes?.takeIf { it > 0 }?.div(30.0)

    /** Quantos meses de aluguel equivalem ao valor do equipamento. */
    val mesesParaValorEquipamento: Double?
        get() {
            val mes = valorMes?.takeIf { it > 0 } ?: return null
            val eq = valorEquipamento?.takeIf { it > 0 } ?: return null
            return eq / mes
        }
}

data class Movimentacao(
    val id: String,
    val ativoId: String,
    val tipo: String,
    val deCentroCustoId: String = "",
    val paraCentroCustoId: String = "",
    val statusResultante: String = "",
    val responsavel: String = "",
    val data: String = "",
    val observacao: String = "",
    val createdAt: Long = 0L,
    val createdBy: String = ""
)

/** grupo: FOTO, DOC (com [item] do checklist), CONTRATO ou NA (item marcado como "não se aplica", sem arquivo). */
data class AnexoAtivo(
    val id: String,
    val ativoId: String,
    val grupo: String,
    val item: String,
    val fileName: String,
    val contentType: String,
    val size: Long,
    val createdAt: Long,
    val createdBy: String
) {
    val ehImagem: Boolean get() = contentType.startsWith("image/")
}

/** Abas do Controle de Ativos liberadas por usuário. "E" edita, "V" só visualiza. null = todas com edição. */
object AtivosEscopo {
    val abas: List<Pair<String, String>> = listOf(
        "PAINEL" to "Painel",
        "DASHBOARD" to "Dashboard",
        "SOLICITACOES" to "Solicitações",
        "ATIVOS" to "Ativos",
        "MOVIMENTACOES" to "Movimentações",
        "CENTROS" to "Obras / Centros de custo",
        "MANUTENCAO" to "Manutenção",
        "FORNECEDORES" to "Fornecedores",
        "LOG" to "Log do módulo"
    )

    fun abasDe(raw: String): Map<String, String>? {
        val s = raw.trim()
        if (s.isEmpty() || s == "*") return null
        return s.split(',').map { it.trim() }.filter { it.isNotEmpty() }.associate { parte ->
            if (parte.endsWith("+")) parte.dropLast(1) to "E" else parte to "V"
        }
    }

    fun codificar(modos: Map<String, String>): String =
        abas.mapNotNull { (chave, _) ->
            when (modos[chave]) {
                "E" -> "$chave+"
                "V" -> chave
                else -> null
            }
        }.joinToString(",")

    fun modosDe(raw: String?): Map<String, String> {
        val gravado = raw?.let { abasDe(it) }
        if (gravado == null) return abas.associate { it.first to "E" }
        return abas.associate { (chave, _) -> chave to (gravado[chave] ?: "") }
    }
}

/** Checklist "Documentos básicos de máquinas e equipamentos" (mesmas chaves do Worker). */
object ChecklistDocsAtivo {
    val itens: List<Pair<String, String>> = listOf(
        "NF_POSSE" to "Nota fiscal e documento de posse do equipamento",
        "MANUAL" to "Manual do fabricante",
        "PLANO_MANUT" to "Plano de manutenção",
        "HRN_NR12" to "Apreciação de risco com metodologia HRN conforme NR 12",
        "LAUDO_CONFORMIDADE" to "Laudo de conformidade",
        "LAUDO_INSPECAO" to "Laudo de inspeção",
        "OS_ULTIMA" to "Ordem de serviço da última intervenção realizada na máquina",
        "REG_MANUT" to "Registro de manutenções",
        "PLACA_ID" to "Placa de identificação da máquina (fabricante, modelo, nº de série)",
        "SIST_PROTECAO" to "Sistema de proteção (grades, enclausuramento, sensores, etc.)",
        "CRLV" to "CRLV",
        "ART" to "ART (Anotação de Responsabilidade Técnica)",
        "CRONOTACOGRAFO" to "Cronotacógrafo",
        "TESTE_CARGA" to "Caminhão Munck / Guindaste - Teste de carga"
    )
}

data class StatusAnexos(val fotos: Int, val docsAtendidos: Int, val contratos: Int) {
    val fotosOk: Boolean get() = fotos >= FOTOS_MINIMAS
    val docsOk: Boolean get() = docsAtendidos >= ChecklistDocsAtivo.itens.size
    val contratoOk: Boolean get() = contratos > 0

    companion object {
        const val FOTOS_MINIMAS = 4
    }
}

data class AtivosDados(
    val versao: Long = 0L,
    val master: Boolean = false,
    /** Supervisor/Engenheiro (ou master): pode excluir ativos e movimentações. */
    val podeExcluir: Boolean = false,
    val centrosCusto: List<CentroCusto> = emptyList(),
    val fornecedores: List<Fornecedor> = emptyList(),
    val ativos: List<Ativo> = emptyList(),
    val movimentacoes: List<Movimentacao> = emptyList(),
    /** Nomes do cadastro de colaboradores (para o campo Responsável). */
    val colaboradores: List<Colaborador> = emptyList(),
    /** Username de quem está logado (minúsculo). */
    val usuario: String = "",
    /** Tipo Engenheiro/Supervisor (ou master). */
    val engSup: Boolean = false,
    val frotasUsername: String = "",
    val frotasEmail: String = "",
    val usuarios: List<UsuarioAtivos> = emptyList(),
    val solicitacoes: List<Solicitacao> = emptyList(),
    val anexos: List<AnexoAtivo> = emptyList(),
    /** null = todas as abas com edição. */
    val abasModo: Map<String, String>? = null,
    /** Usuário comum restrito a centros de custo marcados na matriz. */
    val centrosLimitados: Boolean = false
) {
    private val anexosPorAtivo: Map<String, List<AnexoAtivo>> by lazy { anexos.groupBy { it.ativoId } }

    fun anexosDe(ativoId: String): List<AnexoAtivo> = anexosPorAtivo[ativoId].orEmpty()

    fun podeVer(aba: String): Boolean = abasModo == null || aba in abasModo

    fun podeEditar(aba: String): Boolean = abasModo == null || abasModo[aba] == "E"

    fun statusAnexos(ativoId: String): StatusAnexos {
        val lista = anexosDe(ativoId)
        val comArquivo = lista.filter { it.grupo == "DOC" }.map { it.item }.toSet()
        val naoAplica = lista.filter { it.grupo == "NA" }.map { it.item }.toSet()
        return StatusAnexos(
            fotos = lista.count { it.grupo == "FOTO" },
            docsAtendidos = ChecklistDocsAtivo.itens.count { (k, _) -> k in comArquivo || k in naoAplica },
            contratos = lista.count { it.grupo == "CONTRATO" }
        )
    }

    fun podeRemoverAnexo(a: AnexoAtivo): Boolean = podeExcluir || a.createdBy.equals(usuario, ignoreCase = true)

    private fun centro(id: String): CentroCusto? = centrosCusto.firstOrNull { it.id == id }

    /** Mesma regra do servidor: o Engenheiro/Supervisor da obra; sem definição, qualquer Engenheiro/Supervisor. */
    fun aprovaObra(centroCustoId: String): Boolean {
        if (master) return true
        val eng = centro(centroCustoId)?.engenheiroUsername.orEmpty().lowercase()
        return if (eng.isNotBlank()) eng == usuario else engSup
    }

    fun podeDecidir(s: Solicitacao): Boolean = s.status == "PENDENTE" && aprovaObra(s.centroCustoId)

    fun ehDono(s: Solicitacao): Boolean =
        master || s.solicitante.equals(usuario, true) ||
            centro(s.centroCustoId)?.admUsername.orEmpty().equals(usuario, true) && usuario.isNotBlank()

    val ehFrotas: Boolean get() = master || (frotasUsername.isNotBlank() && frotasUsername.equals(usuario, true))

    fun podeAtender(s: Solicitacao): Boolean = s.status == "AGUARDANDO_FROTAS" && ehFrotas

    /** Solicitações que esperam uma ação de quem está logado. */
    fun pendentesParaMim(): List<Solicitacao> = solicitacoes.filter {
        podeDecidir(it) || podeAtender(it) || (it.status == "DEVOLVIDA" && ehDono(it))
    }

    /** Usuários com TAG ADM que não são administradores do sistema (a quem se delega ativos e obras). */
    val usuariosAdm: List<UsuarioAtivos> get() = usuarios.filter { it.adm && !it.admin }.sortedBy { it.nomeOuLogin.lowercase() }

    fun nomeUsuario(username: String): String =
        usuarios.firstOrNull { it.username.equals(username, true) }?.rotulo ?: username
}

data class Colaborador(val nome: String, val cargo: String = "")

/** Linha do log próprio do Controle de Ativos. */
data class AtivoLog(
    val id: String,
    val createdAt: Long,
    val usuario: String,
    val nome: String,
    val acao: String,
    val alvo: String,
    val detalhe: String
) {
    val acaoRotulo: String get() = rotuloAcao(acao)

    companion object {
        val ACOES = listOf(
            "ATIVO_CREATE", "ATIVO_UPDATE", "ATIVO_DELETE",
            "ATIVO_ANEXO", "ATIVO_ANEXO_NA", "ATIVO_ANEXO_REMOVIDO",
            "ATIVO_MOV", "ATIVO_MOV_DELETE", "ATIVO_FORNECEDOR_SAVE", "ATIVO_CC_SAVE",
            "ATIVO_SOLIC", "ATIVO_SOLIC_ACAO", "ATIVO_CONFIG"
        )

        fun rotuloAcao(acao: String): String = when (acao) {
            "ATIVO_CREATE" -> "Ativo cadastrado"
            "ATIVO_UPDATE" -> "Ativo editado"
            "ATIVO_DELETE" -> "Ativo excluído"
            "ATIVO_ANEXO" -> "Anexo enviado"
            "ATIVO_ANEXO_NA" -> "Anexo não se aplica"
            "ATIVO_ANEXO_REMOVIDO" -> "Anexo removido"
            "ATIVO_MOV" -> "Movimentação"
            "ATIVO_MOV_DELETE" -> "Movimentação excluída"
            "ATIVO_FORNECEDOR_SAVE" -> "Fornecedor salvo"
            "ATIVO_CC_SAVE" -> "Centro de custo salvo"
            "ATIVO_SOLIC" -> "Solicitação criada"
            "ATIVO_SOLIC_ACAO" -> "Solicitação respondida"
            "ATIVO_CONFIG" -> "Configuração do módulo"
            else -> acao
        }
    }
}

object AtivoStatus {
    val todos: List<Pair<String, String>> = listOf(
        "EM_USO" to "Em uso",
        "DISPONIVEL" to "Disponível",
        "MANUTENCAO" to "Em manutenção",
        "DEVOLVIDO" to "Devolvido",
        "BAIXADO" to "Baixado"
    )

    fun rotulo(id: String): String = todos.firstOrNull { it.first == id }?.second ?: id
}

object MovimentacaoTipo {
    /** Tipos que o usuário pode registrar (CADASTRO/EDICAO são automáticos). */
    val registraveis: List<Pair<String, String>> = listOf(
        "TRANSFERENCIA" to "Transferir para outro centro de custo",
        "MANUTENCAO" to "Enviar para manutenção",
        "RETORNO_MANUTENCAO" to "Retorno de manutenção",
        "DEVOLUCAO" to "Devolver ao fornecedor",
        "BAIXA" to "Dar baixa (descarte / perda)",
        "CHEGADA" to "Registrar chegada em obra"
    )

    fun rotulo(id: String): String = when (id) {
        "CHEGADA" -> "Chegada"
        "CADASTRO" -> "Cadastro"
        "TRANSFERENCIA" -> "Transferência"
        "MANUTENCAO" -> "Manutenção"
        "RETORNO_MANUTENCAO" -> "Retorno de manutenção"
        "DEVOLUCAO" -> "Devolução"
        "BAIXA" -> "Baixa"
        "EDICAO" -> "Edição"
        "LOCACAO" -> "Locação de veículo"
        else -> id
    }
}

object AtivoCategorias {
    val todas: List<String> = listOf(
        "Veículo",
        "Veículo pesado",
        "Máquina/Equipamento",
        "Ferramenta",
        "Container",
        "Andaime/Escoramento",
        "Instrumento",
        "Peça/Acessório",
        "Outro"
    )
}

object AtivosFormat {
    fun moeda(v: Double?): String {
        if (v == null) return "—"
        val centavos = (abs(v) * 100).roundToLong()
        val inteiro = (centavos / 100).toString()
        val dec = (centavos % 100).toString().padStart(2, '0')
        val milhar = inteiro.reversed().chunked(3).joinToString(".").reversed()
        return (if (v < 0) "-R$ " else "R$ ") + milhar + "," + dec
    }

    /** Aceita "20.000,00", "20000", "20000.5", "R$ 1.234,56". Vazio → null. */
    fun parseMoeda(s: String): Double? {
        val t = s.replace("R$", "").replace(" ", "").trim()
        if (t.isEmpty()) return null
        val normal = if (t.contains(',')) t.replace(".", "").replace(',', '.') else t
        return normal.toDoubleOrNull()?.takeIf { it >= 0 }
    }

    fun editarMoeda(v: Double?): String = if (v == null) "" else moeda(v).removePrefix("R$ ")

    /** yyyy-MM-dd → dd/MM/yyyy */
    fun dataBr(iso: String): String {
        val p = iso.split("-")
        return if (p.size == 3 && p[0].length == 4) "${p[2]}/${p[1]}/${p[0]}" else iso
    }

    /** dd/MM/yyyy → yyyy-MM-dd ("" se vazio, null se inválida). */
    fun dataIso(br: String): String? {
        val t = br.trim()
        if (t.isEmpty()) return ""
        val p = t.split("/")
        if (p.size != 3) return null
        val d = p[0].toIntOrNull() ?: return null
        val m = p[1].toIntOrNull() ?: return null
        val y = p[2].toIntOrNull() ?: return null
        if (y !in 1990..2100 || m !in 1..12 || d !in 1..31) return null
        return "$y-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"
    }

    fun numero1(v: Double): String {
        val x = (v * 10).roundToLong()
        return "${x / 10},${abs(x % 10)}"
    }
}

/** Cliente das rotas `/ativos/...` do Worker. */
object AtivosApi {
    /** [versao] > 0 → retorna null quando nada mudou desde então (polling ao vivo). */
    suspend fun dados(versao: Long = 0L): Result<AtivosDados?> =
        AuthApi.apiJson("GET", "/ativos/dados" + if (versao > 0) "?versao=$versao" else "")
            .mapCatching { text ->
                val o = JsonMap.parse(text)
                if (o.bool("semMudancas")) return@mapCatching null
                AtivosDados(
                    versao = o.long("versao") ?: 0L,
                    master = o.bool("master"),
                    podeExcluir = o.bool("podeExcluir"),
                    centrosCusto = o.array("centrosCusto").orEmpty().mapNotNull { (it as? JsonMap)?.let(::centroFrom) },
                    fornecedores = o.array("fornecedores").orEmpty().mapNotNull { (it as? JsonMap)?.let(::fornecedorFrom) },
                    ativos = o.array("ativos").orEmpty().mapNotNull { (it as? JsonMap)?.let(::ativoFrom) },
                    movimentacoes = o.array("movimentacoes").orEmpty().mapNotNull { (it as? JsonMap)?.let(::movFrom) },
                    colaboradores = o.array("colaboradores").orEmpty().mapNotNull { x -> (x as? JsonMap)?.let { Colaborador(it.str("nome"), it.str("cargo")) }?.takeIf { it.nome.isNotBlank() } },
                    usuario = o.str("usuario").lowercase(),
                    engSup = o.bool("engSup"),
                    frotasUsername = o.str("frotasUsername"),
                    frotasEmail = o.str("frotasEmail"),
                    usuarios = o.array("usuarios").orEmpty().mapNotNull { x ->
                        (x as? JsonMap)?.let {
                            UsuarioAtivos(it.str("username"), it.str("nome"), it.bool("engSup"), it.bool("adm"), it.bool("liberado", true), it.bool("admin", false))
                        }?.takeIf { it.username.isNotBlank() }
                    },
                    solicitacoes = o.array("solicitacoes").orEmpty().mapNotNull { (it as? JsonMap)?.let(::solicFrom) },
                    anexos = o.array("anexos").orEmpty().mapNotNull { (it as? JsonMap)?.let(::anexoFrom) },
                    abasModo = AtivosEscopo.abasDe(o.str("escopoAbas")),
                    centrosLimitados = o.str("escopoCentros").let { it.isNotEmpty() && it != "*" }
                )
            }

    /** grupo FOTO, DOC (com [item]) ou CONTRATO. Aceita PDF, JPEG ou PNG (fotos: só imagem). */
    suspend fun enviarAnexo(ativoId: String, grupo: String, item: String, fileName: String, bytes: ByteArray): Result<AnexoAtivo> {
        val contentType = when {
            bytes.size > 3 && bytes[0] == '%'.code.toByte() && bytes[1] == 'P'.code.toByte() -> "application/pdf"
            bytes.size > 3 && bytes[0] == 0x89.toByte() -> "image/png"
            else -> "image/jpeg"
        }
        val q = "?grupo=${enc(grupo)}&item=${enc(item)}&fileName=${enc(fileName)}"
        return AuthApi.apiUploadBytes("/ativos/ativos/${enc(ativoId)}/anexos$q", bytes, contentType).mapCatching { text ->
            JsonMap.parse(text).obj("anexo")?.let(::anexoFrom) ?: error("Resposta inválida do servidor")
        }
    }

    suspend fun naoAplica(ativoId: String, item: String, na: Boolean): Result<Unit> {
        val body = buildString {
            append('{')
            campo("item", item)
            append("\"na\":").append(na)
            append('}')
        }
        return AuthApi.apiJson("POST", "/ativos/ativos/${enc(ativoId)}/nao-aplica", body).map { }
    }

    suspend fun baixarAnexo(id: String): Result<ByteArray> =
        AuthApi.apiDownloadBytes("/ativos/anexos/${enc(id)}/arquivo")

    suspend fun removerAnexo(id: String): Result<Unit> =
        AuthApi.apiJson("DELETE", "/ativos/anexos/${enc(id)}", "{}").map { }

    private fun anexoFrom(o: JsonMap): AnexoAtivo? {
        val id = o.str("id").ifBlank { return null }
        return AnexoAtivo(
            id = id,
            ativoId = o.str("ativoId"),
            grupo = o.str("grupo"),
            item = o.str("item"),
            fileName = o.str("fileName"),
            contentType = o.str("contentType"),
            size = o.long("size") ?: 0L,
            createdAt = o.long("createdAt") ?: 0L,
            createdBy = o.str("createdBy")
        )
    }

    suspend fun salvarAtivo(a: Ativo): Result<Ativo> {
        val body = buildString {
            append('{')
            campo("codigo", a.codigo); campo("descricao", a.descricao); campo("modelo", a.modelo)
            campo("categoria", a.categoria); campo("placaPatrimonio", a.placaPatrimonio)
            campo("propriedade", a.propriedade); campo("fornecedorId", a.fornecedorId)
            campo("centroCustoId", a.centroCustoId); campo("responsavel", a.responsavel)
            campo("dataChegada", a.dataChegada); campo("utilizacao", a.utilizacao)
            campo("dataInicioContrato", a.dataInicioContrato); campo("dataTerminoContrato", a.dataTerminoContrato)
            campo("dataEntrega", a.dataEntrega)
            campo("inspecaoTipo", a.inspecaoTipo); campo("status", a.status)
            campo("observacoes", a.observacoes)
            num("valorMes", a.valorMes); num("valorEquipamento", a.valorEquipamento)
            num("valorEmAberto", a.valorEmAberto); num("valorFinalCompra", a.valorFinalCompra, ultimo = true)
            append('}')
        }
        val (metodo, path) = if (a.id.isBlank()) "POST" to "/ativos/ativos" else "PUT" to "/ativos/ativos/${enc(a.id)}"
        return AuthApi.apiJson(metodo, path, body).mapCatching { text ->
            JsonMap.parse(text).obj("ativo")?.let(::ativoFrom) ?: error("Resposta inválida do servidor")
        }
    }

    suspend fun removerAtivo(id: String): Result<Unit> =
        AuthApi.apiJson("DELETE", "/ativos/ativos/${enc(id)}", "{}").map { }

    suspend fun log(): Result<List<AtivoLog>> =
        AuthApi.apiJson("GET", "/ativos/log").mapCatching { text ->
            JsonMap.parse(text).array("logs").orEmpty().mapNotNull { x ->
                val o = x as? JsonMap ?: return@mapNotNull null
                AtivoLog(
                    id = o.str("id"),
                    createdAt = o.long("createdAt") ?: 0L,
                    usuario = o.str("usuario"),
                    nome = o.str("nome"),
                    acao = o.str("acao"),
                    alvo = o.str("alvo"),
                    detalhe = o.str("detalhe")
                )
            }
        }

    suspend fun removerMovimentacao(id: String): Result<Unit> =
        AuthApi.apiJson("DELETE", "/ativos/movimentacoes/${enc(id)}", "{}").map { }

    suspend fun movimentar(
        ativoId: String,
        tipo: String,
        paraCentroCustoId: String,
        responsavel: String,
        dataIso: String,
        observacao: String
    ): Result<Boolean> {
        val body = buildString {
            append('{')
            campo("ativoId", ativoId); campo("tipo", tipo); campo("paraCentroCustoId", paraCentroCustoId)
            campo("responsavel", responsavel); campo("data", dataIso)
            campo("observacao", observacao, ultimo = true)
            append('}')
        }
        /** true = aplicada direto; false = virou solicitação aguardando aprovação. */
        return AuthApi.apiJson("POST", "/ativos/movimentacoes", body).mapCatching { text ->
            JsonMap.parse(text).bool("aplicada", true)
        }
    }

    suspend fun solicitarLocacao(
        centroCustoId: String,
        veiculo: String,
        dataInicioIso: String,
        dataFimIso: String,
        justificativa: String,
        condutor: String,
        observacao: String
    ): Result<Solicitacao> {
        val body = buildString {
            append('{')
            campo("centroCustoId", centroCustoId); campo("veiculo", veiculo)
            campo("dataInicio", dataInicioIso); campo("dataFim", dataFimIso)
            campo("justificativa", justificativa); campo("condutor", condutor)
            campo("observacao", observacao, ultimo = true)
            append('}')
        }
        return AuthApi.apiJson("POST", "/ativos/solicitacoes", body).mapCatching { text ->
            JsonMap.parse(text).obj("solicitacao")?.let(::solicFrom) ?: error("Resposta inválida do servidor")
        }
    }

    /** acao: APROVAR, REPROVAR, DEVOLVER, REENVIAR, CANCELAR, ATENDER. */
    suspend fun acaoSolicitacao(id: String, acao: String, obs: String): Result<Solicitacao> {
        val body = buildString {
            append('{')
            campo("acao", acao); campo("obs", obs, ultimo = true)
            append('}')
        }
        return AuthApi.apiJson("POST", "/ativos/solicitacoes/${enc(id)}/acao", body).mapCatching { text ->
            JsonMap.parse(text).obj("solicitacao")?.let(::solicFrom) ?: error("Resposta inválida do servidor")
        }
    }

    suspend fun salvarConfig(frotasUsername: String, frotasEmail: String): Result<Unit> {
        val body = buildString {
            append('{')
            campo("frotasUsername", frotasUsername); campo("frotasEmail", frotasEmail, ultimo = true)
            append('}')
        }
        return AuthApi.apiJson("PUT", "/ativos/config", body).map { }
    }

    suspend fun salvarFornecedor(f: Fornecedor): Result<Fornecedor> {
        val body = buildString {
            append('{')
            campo("nome", f.nome); campo("cnpj", f.cnpj); campo("contato", f.contato)
            campo("telefone", f.telefone); campo("email", f.email); campo("observacoes", f.observacoes)
            append("\"ativo\":").append(f.ativo)
            append('}')
        }
        val (metodo, path) = if (f.id.isBlank()) "POST" to "/ativos/fornecedores" else "PUT" to "/ativos/fornecedores/${enc(f.id)}"
        return AuthApi.apiJson(metodo, path, body).mapCatching { text ->
            JsonMap.parse(text).obj("fornecedor")?.let(::fornecedorFrom) ?: error("Resposta inválida do servidor")
        }
    }

    suspend fun salvarCentroCusto(c: CentroCusto): Result<CentroCusto> {
        val body = buildString {
            append('{')
            campo("codigo", c.codigo); campo("nome", c.nome)
            campo("engenheiroUsername", c.engenheiroUsername); campo("admUsername", c.admUsername)
            append("\"ativo\":").append(c.ativo)
            append('}')
        }
        val (metodo, path) = if (c.id.isBlank()) "POST" to "/ativos/centros-custo" else "PUT" to "/ativos/centros-custo/${enc(c.id)}"
        return AuthApi.apiJson(metodo, path, body).mapCatching { text ->
            JsonMap.parse(text).obj("centroCusto")?.let(::centroFrom) ?: error("Resposta inválida do servidor")
        }
    }

    private fun centroFrom(o: JsonMap): CentroCusto? {
        val id = o.str("id").ifBlank { return null }
        return CentroCusto(
            id = id,
            codigo = o.str("codigo"),
            nome = o.str("nome"),
            ativo = o.bool("ativo", true),
            engenheiroUsername = o.str("engenheiroUsername"),
            admUsername = o.str("admUsername")
        )
    }

    private fun solicFrom(o: JsonMap): Solicitacao? {
        val id = o.str("id").ifBlank { return null }
        return Solicitacao(
            id = id,
            tipo = o.str("tipo"),
            status = o.str("status"),
            centroCustoId = o.str("centroCustoId"),
            ativoId = o.str("ativoId"),
            movTipo = o.str("movTipo"),
            paraCentroCustoId = o.str("paraCentroCustoId"),
            responsavel = o.str("responsavel"),
            data = o.str("data"),
            observacao = o.str("observacao"),
            veiculo = o.str("veiculo"),
            dataInicio = o.str("dataInicio"),
            dataFim = o.str("dataFim"),
            justificativa = o.str("justificativa"),
            condutor = o.str("condutor"),
            solicitante = o.str("solicitante"),
            solicitanteNome = o.str("solicitanteNome"),
            historico = o.array("historico").orEmpty().mapNotNull { x ->
                (x as? JsonMap)?.let { SolicEvento(it.long("em") ?: 0L, it.str("por"), it.str("acao"), it.str("obs")) }
            },
            createdAt = o.long("createdAt") ?: 0L,
            updatedAt = o.long("updatedAt") ?: 0L
        )
    }

    private fun fornecedorFrom(o: JsonMap): Fornecedor? {
        val id = o.str("id").ifBlank { return null }
        return Fornecedor(
            id = id,
            nome = o.str("nome"),
            cnpj = o.str("cnpj"),
            contato = o.str("contato"),
            telefone = o.str("telefone"),
            email = o.str("email"),
            observacoes = o.str("observacoes"),
            ativo = o.bool("ativo", true)
        )
    }

    private fun ativoFrom(o: JsonMap): Ativo? {
        val id = o.str("id").ifBlank { return null }
        return Ativo(
            id = id,
            codigo = o.str("codigo"),
            descricao = o.str("descricao"),
            modelo = o.str("modelo"),
            categoria = o.str("categoria"),
            placaPatrimonio = o.str("placaPatrimonio"),
            propriedade = o.str("propriedade").ifBlank { "ALUGADO" },
            fornecedorId = o.str("fornecedorId"),
            centroCustoId = o.str("centroCustoId"),
            responsavel = o.str("responsavel"),
            dataChegada = o.str("dataChegada"),
            dataInicioContrato = o.str("dataInicioContrato"),
            dataTerminoContrato = o.str("dataTerminoContrato"),
            dataEntrega = o.str("dataEntrega"),
            valorMes = o.double("valorMes"),
            valorEquipamento = o.double("valorEquipamento"),
            valorEmAberto = o.double("valorEmAberto"),
            valorFinalCompra = o.double("valorFinalCompra"),
            utilizacao = o.str("utilizacao"),
            inspecaoTipo = o.str("inspecaoTipo"),
            status = o.str("status").ifBlank { "EM_USO" },
            observacoes = o.str("observacoes"),
            updatedAt = o.long("updatedAt") ?: 0L
        )
    }

    private fun movFrom(o: JsonMap): Movimentacao? {
        val id = o.str("id").ifBlank { return null }
        return Movimentacao(
            id = id,
            ativoId = o.str("ativoId"),
            tipo = o.str("tipo"),
            deCentroCustoId = o.str("deCentroCustoId"),
            paraCentroCustoId = o.str("paraCentroCustoId"),
            statusResultante = o.str("statusResultante"),
            responsavel = o.str("responsavel"),
            data = o.str("data"),
            observacao = o.str("observacao"),
            createdAt = o.long("createdAt") ?: 0L,
            createdBy = o.str("createdBy")
        )
    }

    private fun StringBuilder.campo(k: String, v: String, ultimo: Boolean = false) {
        append('"').append(k).append("\":").append(jsonStr(v))
        if (!ultimo) append(',')
    }

    private fun StringBuilder.num(k: String, v: Double?, ultimo: Boolean = false) {
        append('"').append(k).append("\":").append(v?.toString() ?: "null")
        if (!ultimo) append(',')
    }

    private fun jsonStr(s: String): String = buildString {
        append('"')
        for (c in s) {
            when (c) {
                '\\' -> append("\\\\")
                '"' -> append("\\\"")
                '\n' -> append("\\n")
                '\r' -> append("\\r")
                '\t' -> append("\\t")
                else -> if (c.code < 0x20) append("\\u" + c.code.toString(16).padStart(4, '0')) else append(c)
            }
        }
        append('"')
    }

    private fun enc(s: String): String = buildString {
        for (b in s.encodeToByteArray()) {
            val c = b.toInt().toChar()
            if (c.isLetterOrDigit() && b >= 0 || c == '-' || c == '_' || c == '.' || c == '~') append(c)
            else append('%').append(((b.toInt() and 0xFF).toString(16).uppercase()).padStart(2, '0'))
        }
    }
}
